import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useI18n } from "../contexts/I18nContext";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  LayoutDashboard,
  Server,
  Radio,
  Building2,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  LogOut,
  User,
  ChevronDown,
  Zap,
  Upload,
  Target,
  Bell,
  FlaskConical,
  Users,
  FileText,
  Settings,
  FileBarChart,
  Activity,
  BarChart3,
  PieChart,
  Gauge,
  Receipt,
  ClipboardList,
  ListFilter,
  MapPin,
} from "lucide-react";

const navItems = [
  { path: "/", label: "Dashboard", tKey: "nav.dashboard", icon: LayoutDashboard },
  { path: "/analytics", label: "Analytics", tKey: "nav.analytics", icon: BarChart3 },
  { path: "/billing", label: "Billing", tKey: "nav.billing", icon: Receipt },
  { path: "/reporting", label: "Reports", tKey: "nav.reports", icon: ClipboardList },
  { path: "/performance", label: "Performance", tKey: "nav.performance", icon: Gauge, role: "admin" },
  { path: "/stats", label: "Stats", tKey: "nav.stats", icon: PieChart, role: "admin" },
  { path: "/ssps", label: "SSP Endpoints", tKey: "nav.ssp_endpoints", icon: Server },
  { path: "/dsps", label: "DSP Endpoints", tKey: "nav.dsp_endpoints", icon: Radio },
  { path: "/dsp-health", label: "DSP Health", tKey: "nav.dsp_health", icon: Activity, role: "admin" },
  { path: "/companies", label: "Companies", tKey: "nav.companies", icon: Building2, role: "admin" },
  { path: "/simulation", label: "Bid Simulation", tKey: "nav.bid_simulation", icon: Zap, role: "admin" },
  { path: "/optimizer", label: "Floor Optimizer", tKey: "nav.floor_optimizer", icon: Target, role: "admin" },
  { path: "/ab-testing", label: "A/B Testing", tKey: "nav.ab_testing", icon: FlaskConical, role: "admin" },
  { path: "/alerts", label: "Alerts", tKey: "nav.alerts", icon: Bell, role: "admin" },
  { path: "/import", label: "Bulk Import", tKey: "nav.bulk_import", icon: Upload, role: "admin" },
  { path: "/list-manager", label: "List Manager", tKey: "nav.list_manager", icon: ListFilter, role: "admin" },
  { path: "/footfall", label: "Footfall", tKey: "nav.footfall", icon: MapPin, role: "admin" },
  { path: "/audience", label: "Audience", tKey: "nav.audience", icon: Users, role: "admin" },
  { divider: true },
  { path: "/scheduled-reports", label: "Scheduled Reports", tKey: "nav.scheduled_reports", icon: FileBarChart, role: "admin" },
  { path: "/users", label: "User Management", tKey: "nav.user_management", icon: Users, role: "admin" },
  { path: "/audit-logs", label: "Audit Logs", tKey: "nav.audit_logs", icon: FileText, role: "admin" },
  { path: "/settings", label: "Settings", tKey: "nav.settings", icon: Settings, role: "admin" },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, branding, isSuperAdmin, isAdmin } = useAuth();
  const { theme, setTheme, actualTheme } = useTheme();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const ThemeIcon = actualTheme === "dark" ? Moon : Sun;

  // Apply branding
  const sidebarTitle = branding?.sidebar_title || "oRTB Exchange";
  const primaryColor = branding?.primary_color || null;

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-card border-r transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="flex h-16 items-center justify-between px-4 border-b">
          <div className="flex items-center gap-2">
            <img src="/logo192.png" alt="Logo" className="h-8 w-8 rounded-lg" />
            <span className="font-bold text-lg tracking-tight">{sidebarTitle}</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <ScrollArea className="h-[calc(100vh-4rem)] py-4">
          <nav className="space-y-1 px-3">
            {navItems.map((item, index) => {
              if (item.divider) {
                return <div key={`divider-${index}`} className="my-2 border-t border-border" />;
              }
              
              // Role-based visibility
              const userRole = user?.role || "user";
              if (item.role === "admin" && userRole === "user") return null;
              if (item.role === "super_admin" && userRole !== "super_admin") return null;
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : "inactive"}`
                  }
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <item.icon className="h-5 w-5" />
                  {t(item.tKey, item.label)}
                </NavLink>
              );
            })}
          </nav>
        </ScrollArea>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
          <div className="flex h-full items-center justify-between px-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              {/* Theme toggle */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="theme-toggle">
                    <ThemeIcon className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setTheme("light")}>
                    <Sun className="mr-2 h-4 w-4" />
                    Light
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("dark")}>
                    <Moon className="mr-2 h-4 w-4" />
                    Dark
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("system")}>
                    <Monitor className="mr-2 h-4 w-4" />
                    System
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu">
                    <div className="h-8 w-8 rounded-full bg-accent/20 flex items-center justify-center">
                      <User className="h-4 w-4 text-accent" />
                    </div>
                    <span className="hidden md:inline-block text-sm font-medium">
                      {user?.name}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span>{user?.name}</span>
                      <span className="text-xs font-normal text-muted-foreground">
                        {user?.email}
                      </span>
                      <span className="text-xs font-medium mt-1 capitalize" style={{ color: primaryColor || "hsl(var(--accent))" }}>
                        {user?.role?.replace("_", " ")}
                        {user?.company_name ? ` · ${user.company_name}` : ""}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="container mx-auto px-4 py-8 max-w-[1600px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
